import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import axios from 'axios';
import { useAccount, useConnect, useDisconnect, useChainId, useWriteContract, useWaitForTransactionReceipt, useSendTransaction, useSwitchChain } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment recipient address (same on all chains)
const PAYMENT_ADDRESS = '0x4dA2e85D64bEcE663CCaB06E89B970b6b077f22F';

// Supported chains configuration
const SUPPORTED_CHAINS = {
  ethereum: { id: 1, name: 'Ethereum' },
  polygon: { id: 137, name: 'Polygon' }
};

// Chain name to ID mapping
const getChainId = (chainName) => {
  return SUPPORTED_CHAINS[chainName]?.id || 1;
};

const getChainName = (chainId) => {
  switch (chainId) {
    case 1: return 'ethereum';
    case 137: return 'polygon';
    default: return 'ethereum';
  }
};

// ERC20 ABI for approve and transfer functions
const ERC20_ABI = [
  {
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function'
  }
];

// Proper wagmi v2 wallet connector component
const WalletConnector = () => {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isPending, error } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const availableConnectors = connectors.filter((connector) => connector.id === 'injected');
  const injectedConnector = availableConnectors[0] || connectors[0];
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletProviders, setWalletProviders] = useState([]);
  const [walletStatus, setWalletStatus] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleAnnounce = (event) => {
      const detail = event?.detail;
      if (!detail?.info?.uuid) return;
      setWalletProviders((prev) => {
        if (prev.some((p) => p.info.uuid === detail.info.uuid)) return prev;
        return [...prev, detail];
      });
    };

    window.addEventListener('eip6963:announceProvider', handleAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    return () => window.removeEventListener('eip6963:announceProvider', handleAnnounce);
  }, []);

  const getChainName = (chainId) => {
    switch (chainId) {
      case 1: return 'ethereum';
      case 137: return 'polygon';
      default: return 'ethereum';
    }
  };

  if (isConnected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-green-800">
                Connected: {address?.slice(0, 6)}...{address?.slice(-4)}
              </p>
              <p className="text-xs text-green-600">
                Network: {getChainName(chainId)}
              </p>
            </div>
          </div>
          <button
            onClick={() => disconnect()}
            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  const preferredUuid = (() => {
    try {
      return localStorage.getItem('pinguiWalletUuid') || '';
    } catch {
      return '';
    }
  })();

  const walletChoices = (() => {
    const choices = walletProviders.slice();
    if (choices.length === 0 && typeof window !== 'undefined' && window.ethereum) {
      choices.push({
        info: { name: 'Browser Wallet' },
        provider: window.ethereum,
      });
    }
    return choices;
  })();

  const connectWithProvider = async (choice) => {
    if (!injectedConnector) {
      setWalletStatus('No injected connector available.');
      return;
    }
    setWalletStatus('Connecting...');
    if (choice?.info?.uuid) {
      try {
        localStorage.setItem('pinguiWalletUuid', choice.info.uuid);
      } catch {}
    }
    if (choice?.provider) {
      window.__PINGUI_SELECTED_PROVIDER = choice.provider;
    }
    try {
      await connect({ connector: injectedConnector });
      setWalletModalOpen(false);
      setWalletStatus('');
    } catch (err) {
      setWalletStatus(err?.shortMessage || err?.message || 'Failed to connect');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error.message}</p>
        </div>
      )}
      
      <button
        onClick={() => setWalletModalOpen(true)}
        disabled={isPending}
        className="w-full flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-white">W</span>
        </div>
        <span className="font-medium text-gray-800">Choose Wallet</span>
        {isPending && (
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        )}
      </button>
      
      <p className="text-xs text-gray-500 text-center">
        💡 Supports MetaMask, Rabby, Phantom, and other injected wallets
      </p>

      {walletModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Choose Wallet</h3>
              <button
                onClick={() => setWalletModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {walletChoices.length === 0 ? (
              <div className="p-4 border border-gray-200 rounded-lg text-sm text-gray-600">
                No wallet providers detected. Install a wallet extension and refresh the page.
              </div>
            ) : (
              <div className="space-y-2">
                {walletChoices.map((choice) => {
                  const isPreferred = choice?.info?.uuid && choice.info.uuid === preferredUuid;
                  return (
                    <button
                      key={choice?.info?.uuid || choice?.info?.name || 'browser-wallet'}
                      onClick={() => connectWithProvider(choice)}
                      className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                    >
                      {choice?.info?.icon ? (
                        <img src={choice.info.icon} alt="" className="w-7 h-7 rounded" />
                      ) : (
                        <div className="w-7 h-7 bg-gray-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">W</span>
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-800">{choice?.info?.name || 'Browser Wallet'}</div>
                        <div className="text-xs text-gray-500">Injected provider</div>
                      </div>
                      {isPreferred && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          preferred
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {walletStatus && (
              <div className="mt-3 text-sm text-gray-600">{walletStatus}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Payment processing component
const PaymentProcessor = ({ cost, selectedToken, tokens, onPaymentSuccess, onPaymentError }) => {
  const [paymentStep, setPaymentStep] = useState('ready');
  const [approveHash, setApproveHash] = useState(null);
  const [transferHash, setTransferHash] = useState(null);
  
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  
  // Contract write hooks for ERC20
  const { writeContract: writeApprove, data: approveData, isPending: approvePending } = useWriteContract();
  const { writeContract: writeTransfer, data: transferData, isPending: transferPending } = useWriteContract();
  
  // Direct ETH transfer
  const { sendTransaction, data: ethData, isPending: ethPending } = useSendTransaction();
  
  // Transaction receipt hooks
  const { isLoading: approveLoading, isSuccess: approveSuccess } = useWaitForTransactionReceipt({
    hash: approveData
  });
  
  const { isLoading: transferLoading, isSuccess: transferSuccess } = useWaitForTransactionReceipt({
    hash: transferData
  });
  
  const { isLoading: ethLoading, isSuccess: ethSuccess } = useWaitForTransactionReceipt({
    hash: ethData
  });

  const token = tokens.find(t => t.id === selectedToken);
  const isNativeToken = selectedToken?.endsWith('-0x0');

  // Handle transaction success
  useEffect(() => {
    if (ethSuccess && ethData) {
      setTransferHash(ethData);
      setPaymentStep('completed');
      onPaymentSuccess({
        type: 'native',
        totalHash: ethData,
        transferHash: ethData
      });
    }
  }, [ethSuccess, ethData, onPaymentSuccess]);

  useEffect(() => {
    if (approveSuccess && approveData) {
      setApproveHash(approveData);
      setPaymentStep('transferring');
      
      // Auto-initiate transfer after approve
      setTimeout(() => {
        handleTransfer();
      }, 2000);
    }
  }, [approveSuccess, approveData]);

  useEffect(() => {
    if (transferSuccess && transferData) {
      setTransferHash(transferData);
      setPaymentStep('completed');
      onPaymentSuccess({
        type: 'erc20',
        approveHash,
        transferHash: transferData,
        totalHash: transferData
      });
    }
  }, [transferSuccess, transferData, approveHash, onPaymentSuccess]);

  const handleApprove = async () => {
    if (!token || isNativeToken) return;
    
    try {
      setPaymentStep('approving');
      
      writeApprove({
        address: token.address,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [PAYMENT_ADDRESS, BigInt(cost.token_amount)]
      });
      
    } catch (error) {
      console.error('Approve failed:', error);
      setPaymentStep('error');
      onPaymentError(error);
    }
  };

  const handleTransfer = async () => {
    if (!token) return;
    
    try {
      if (isNativeToken) {
        // Direct ETH/native token transfer
        setPaymentStep('paying');
        
        sendTransaction({
          to: PAYMENT_ADDRESS,
          value: BigInt(cost.token_amount)
        });
      } else {
        // ERC20 transfer
        writeTransfer({
          address: token.address,
          abi: ERC20_ABI,
          functionName: 'transfer',
          args: [PAYMENT_ADDRESS, BigInt(cost.token_amount)]
        });
      }
    } catch (error) {
      console.error('Transfer failed:', error);
      setPaymentStep('error');
      onPaymentError(error);
    }
  };

  const handlePayment = async () => {
    if (!isConnected || !token || !cost) return;
    
    if (isNativeToken) {
      // Direct payment for native tokens
      await handleTransfer();
    } else {
      // Two-step process for ERC20
      await handleApprove();
    }
  };

  if (!token) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-800">Complete Payment</h2>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Amount to pay:</span>
            <span className="font-medium">{cost.token_amount_formatted} {token.symbol}</span>
          </div>
          <div className="flex justify-between">
            <span>USD value:</span>
            <span>${cost.usd_cost.toFixed(6)}</span>
          </div>
          <div className="flex justify-between">
            <span>Payment method:</span>
            <span>{token.symbol} on {token.chainName}</span>
          </div>
        </div>
      </div>

      {paymentStep === 'ready' && (
        <button
          onClick={handlePayment}
          disabled={!isConnected || !cost}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {isNativeToken ? `Pay ${cost.token_amount_formatted} ${token.symbol}` : `Approve & Transfer ${cost.token_amount_formatted} ${token.symbol}`}
        </button>
      )}

      {paymentStep !== 'ready' && paymentStep !== 'completed' && (
        <div className="space-y-3">
          {/* Payment Steps Progress */}
          <div className="space-y-2">
            {!isNativeToken && (
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${
                paymentStep === 'approving' ? 'bg-yellow-50 border border-yellow-200' :
                approveHash ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  approveHash ? 'bg-green-500' : 
                  paymentStep === 'approving' ? 'bg-yellow-500' : 'bg-gray-300'
                }`}>
                  {approveHash ? (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : paymentStep === 'approving' ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span className="text-white text-xs">1</span>
                  )}
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">Approve Token</span>
                  {paymentStep === 'approving' && <span className="text-xs text-yellow-600 ml-2">Confirm in wallet...</span>}
                </div>
                {approveHash && (
                  <a
                    href={`https://etherscan.io/tx/${approveHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-xs"
                  >
                    {approveHash.slice(0, 8)}...{approveHash.slice(-6)}
                  </a>
                )}
              </div>
            )}

            <div className={`flex items-center space-x-3 p-3 rounded-lg ${
              paymentStep === 'transferring' || paymentStep === 'paying' ? 'bg-yellow-50 border border-yellow-200' :
              transferHash ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                transferHash ? 'bg-green-500' : 
                paymentStep === 'transferring' || paymentStep === 'paying' ? 'bg-yellow-500' : 'bg-gray-300'
              }`}>
                {transferHash ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (paymentStep === 'transferring' || paymentStep === 'paying') ? (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span className="text-white text-xs">{isNativeToken ? '1' : '2'}</span>
                )}
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium">{isNativeToken ? 'Send Payment' : 'Transfer Tokens'}</span>
                {(paymentStep === 'transferring' || paymentStep === 'paying') && <span className="text-xs text-yellow-600 ml-2">Processing...</span>}
              </div>
              {transferHash && (
                <a
                  href={`https://etherscan.io/tx/${transferHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-xs"
                >
                  {transferHash.slice(0, 8)}...{transferHash.slice(-6)}
                </a>
              )}
            </div>
          </div>

          {/* Loading indicators */}
          {(approvePending || transferPending || ethPending || approveLoading || transferLoading || ethLoading) && (
            <div className="flex items-center justify-center space-x-2 text-blue-600">
              <div className="w-4 h-4 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">
                {approvePending && 'Confirm approval in wallet...'}
                {transferPending && 'Confirm transfer in wallet...'}
                {ethPending && 'Confirm payment in wallet...'}
                {(approveLoading || transferLoading || ethLoading) && 'Transaction processing...'}
              </span>
            </div>
          )}
        </div>
      )}

      {paymentStep === 'completed' && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">Payment Successful!</p>
              <p className="text-xs text-green-600">Upload will begin automatically</p>
            </div>
            {transferHash && (
              <div className="text-right">
                <a
                  href={`https://etherscan.io/tx/${transferHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {transferHash.slice(0, 8)}...{transferHash.slice(-6)}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Debug info */}
      {paymentStep === 'error' && (
        <div className="text-xs text-red-600">
          Payment failed. Please try again or switch to a different token.
        </div>
      )}
    </div>
  );
};



const TokenSelector = ({ tokens, selectedToken, onSelect, disabled }) => {
  if (!tokens.length) return null;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Payment Token (All Chains)
      </label>
      <select
        value={selectedToken}
        onChange={(e) => onSelect(e.target.value)}
        disabled={disabled}
        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
      >
        <option value="">Select a token from any chain</option>
        {tokens.map((token) => (
          <option key={`${token.id}-${token.chainId}`} value={token.id}>
            {token.balance} {token.symbol} on {token.chainName} - ${token.usd_price.toFixed(4)} | ${token.balance_usd} USD
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500">
        💡 Showing tokens from all chains. Your wallet will switch automatically.
      </p>
    </div>
  );
};

const FileUploader = ({ onFileSelect, selectedFile }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        dragOver
          ? 'border-blue-400 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400'
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div className="space-y-4">
        <div className="mx-auto w-12 h-12 text-gray-400">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        {selectedFile ? (
          <div>
            <p className="text-sm text-gray-600">Selected file:</p>
            <p className="font-medium">{selectedFile.name}</p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        ) : (
          <div>
            <p className="text-lg font-medium text-gray-700">
              Drop your file here or click to browse
            </p>
            <p className="text-sm text-gray-500">
              Any file type supported
            </p>
          </div>
        )}
        <input
          type="file"
          onChange={(e) => {
            if (e.target.files[0]) {
              onFileSelect(e.target.files[0]);
            }
          }}
          className="hidden"
          id="file-input"
        />
        <label
          htmlFor="file-input"
          className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
        >
          Browse Files
        </label>
      </div>
    </div>
  );
};

const CostCalculator = ({ file, selectedToken, tokens, cost, loading }) => {
  if (!file || !selectedToken || loading) return null;

  const token = tokens.find(t => t.id === selectedToken);
  if (!token) return null;

  return (
    <div className="bg-gray-50 p-4 rounded-lg border">
      <h3 className="font-medium text-gray-800 mb-3">Upload Cost</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>File size:</span>
          <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
        </div>
        <div className="flex justify-between">
          <span>Cost (USD):</span>
          <span>${cost?.usd_cost?.toFixed(6) || '0.00'}</span>
        </div>
        <div className="flex justify-between font-medium">
          <span>Pay with {token.symbol}:</span>
          <span>{cost?.token_amount_formatted || '0'} {token.symbol}</span>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>{token.symbol} price:</span>
          <span>${token.usd_price.toFixed(4)}</span>
        </div>
      </div>
    </div>
  );
};

const UploadProgress = ({ uploading, progress, status }) => {
  if (!uploading) return null;

  return (
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <div className="flex items-center space-x-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-800">{status}</p>
          <div className="w-full bg-blue-200 rounded-full h-2 mt-1">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadResult = ({ result, onNewUpload }) => {
  if (!result) return null;

  return (
    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
      <div className="flex items-start space-x-3">
        <div className="w-6 h-6 text-green-600">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-green-800 mb-2">Upload Successful!</h3>
          <div className="space-y-2 text-sm">
            <p><strong>IPFS Hash:</strong> {result.ipfs_hash}</p>
            <p><strong>File Size:</strong> {(result.file_size / 1024 / 1024).toFixed(2)} MB</p>
            <p><strong>Cost:</strong> ${result.upload_cost_usd.toFixed(6)}</p>
          </div>
          
          <div className="mt-4">
            <p className="text-sm font-medium text-green-800 mb-2">Access your file:</p>
            <div className="space-y-1">
              {result.gateway_urls.slice(0, 2).map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-600 hover:text-blue-800 underline break-all"
                >
                  {url}
                </a>
              ))}
            </div>
            
            {/* IPFSviewer.com link with new format */}
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <a
                href={`https://ipfsviewer.com?hash=${result.ipfs_hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm text-blue-700 hover:text-blue-900 font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View your upload on IPFSviewer.com
              </a>
            </div>
          </div>
          
          <button
            onClick={onNewUpload}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Upload Another File
          </button>
        </div>
      </div>
    </div>
  );
};

// FAQ Section Component
const FAQSection = () => {
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqs = [
    {
      question: "What is this?",
      answer: "Pingui is a Web3 application that lets you upload any file to IPFS (InterPlanetary File System) and pay for it with cryptocurrency. Once uploaded, your files are pinned on IPFS forever, giving you decentralized, permanent storage that you can access from anywhere. Simply connect your wallet, select a file, pay with crypto, and get your IPFS link instantly."
    },
    {
      question: "How are my files pinned?",
      answer: "We use two reliable IPFS pinning services to ensure your files stay available forever. Your files are automatically pinned on both Pinata and Filebase, providing redundancy and reliability. This dual-pinning approach means your files will remain accessible even if one service experiences issues."
    },
    {
      question: "Which chains and tokens do you support?",
      answer: "Currently, we support Ethereum and Polygon networks. You can pay with ETH (Ethereum), POL (Polygon), WETH (Wrapped Ethereum), USDT (Tether), and USDC (USD Coin) on both chains. The app automatically detects your wallet balances across all supported chains and lets you pay with any available token."
    },
    {
      question: "How to get in touch?",
      answer: (
        <span>
          Have questions or feedback? Tweet at me{' '}
          <a 
            href="https://x.com/bitfalls" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            @bitfalls
          </a>
          {' '}on X (Twitter). I'm always happy to help with any issues or discuss new features for the platform.
        </span>
      )
    }
  ];

  return (
    <section className="max-w-2xl mx-auto mb-16">
      <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <h3>
              <button
                onClick={() => setOpenQuestion(openQuestion === index ? null : index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-800">{faq.question}</span>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    openQuestion === index ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </h3>
            {openQuestion === index && (
              <div className="px-6 pb-4">
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// Admin Panel Component
const AdminPanel = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('pricing');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [adminToken, setAdminToken] = useState('');
  const [settings, setSettings] = useState(null);
  const [newPricing, setNewPricing] = useState({ base_fee: '', per_mb_fee: '' });
  const [newToken, setNewToken] = useState({
    chain: '', address: '', symbol: '', name: '', decimals: '', coingecko_id: ''
  });

  const adminHeaders = adminToken ? { 'X-Admin-Token': adminToken } : {};

  const authenticateAdmin = async () => {
    try {
      await axios.post(`${API}/admin/login`, credentials, { headers: adminHeaders });
      setIsAuthenticated(true);
      fetchSettings();
    } catch (error) {
      alert('Login failed. Check username, password, and admin token.');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { headers: adminHeaders });
      setSettings(response.data);
      setNewPricing({
        base_fee: response.data.pricing.base_upload_fee_usd,
        per_mb_fee: response.data.pricing.per_mb_fee_usd
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const updatePricing = async () => {
    try {
      const formData = new FormData();
      formData.append('base_fee', newPricing.base_fee);
      formData.append('per_mb_fee', newPricing.per_mb_fee);
      formData.append('admin_user', credentials.username);

      await axios.post(`${API}/admin/pricing`, formData, { headers: adminHeaders });
      alert('Pricing updated successfully!');
      fetchSettings();
    } catch (error) {
      alert('Failed to update pricing');
    }
  };

  const addToken = async () => {
    try {
      const formData = new FormData();
      Object.keys(newToken).forEach(key => {
        formData.append(key, newToken[key]);
      });
      formData.append('admin_user', credentials.username);

      await axios.post(`${API}/admin/tokens`, formData, { headers: adminHeaders });
      alert('Token added successfully!');
      setNewToken({ chain: '', address: '', symbol: '', name: '', decimals: '', coingecko_id: '' });
      fetchSettings();
    } catch (error) {
      alert('Failed to add token');
    }
  };

  const fetchTokenInfo = async () => {
    if (!newToken.chain || !newToken.address) {
      alert('Please select chain and enter token address first');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/admin/token-info/${newToken.chain}/${newToken.address}`,
        { headers: adminHeaders }
      );
      const tokenInfo = response.data.token_info;
      
      setNewToken({
        ...newToken,
        symbol: tokenInfo.symbol || newToken.symbol,
        name: tokenInfo.name || newToken.name,
        decimals: tokenInfo.decimals || newToken.decimals
      });
      
      alert('Token info fetched from blockchain!');
    } catch (error) {
      alert('Failed to fetch token info from blockchain');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Admin Login</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
          </div>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              className="w-full p-3 border rounded-lg"
            />
            <input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              className="w-full p-3 border rounded-lg"
            />
            <input
              type="password"
              placeholder="Admin token (if required)"
              value={adminToken}
              onChange={(e) => setAdminToken(e.target.value)}
              className="w-full p-3 border rounded-lg"
            />
            <button
              onClick={authenticateAdmin}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Admin Panel</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b">
          {['pricing', 'tokens', 'chains'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 px-1 capitalize ${
                activeTab === tab 
                  ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Update Pricing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Base Fee (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPricing.base_fee}
                  onChange={(e) => setNewPricing({...newPricing, base_fee: e.target.value})}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Per MB Fee (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPricing.per_mb_fee}
                  onChange={(e) => setNewPricing({...newPricing, per_mb_fee: e.target.value})}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
            </div>
            <button
              onClick={updatePricing}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Update Pricing
            </button>
          </div>
        )}

        {/* Tokens Tab */}
        {activeTab === 'tokens' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Add New Token</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Chain</label>
                  <select
                    value={newToken.chain}
                    onChange={(e) => setNewToken({...newToken, chain: e.target.value})}
                    className="w-full p-3 border rounded-lg"
                  >
                    <option value="">Select Chain</option>
                    {settings?.chains && settings.chains.map((chain) => (
                      <option key={chain.chain_id} value={chain.chain_id}>
                        {chain.name} ({chain.chain_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contract Address</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="0x..."
                      value={newToken.address}
                      onChange={(e) => setNewToken({...newToken, address: e.target.value})}
                      className="flex-1 p-3 border rounded-lg"
                    />
                    <button
                      onClick={fetchTokenInfo}
                      disabled={!newToken.chain || !newToken.address}
                      className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      Fetch Info
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Symbol (e.g., USDT)"
                  value={newToken.symbol}
                  onChange={(e) => setNewToken({...newToken, symbol: e.target.value})}
                  className="p-3 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Name (e.g., Tether USD)"
                  value={newToken.name}
                  onChange={(e) => setNewToken({...newToken, name: e.target.value})}
                  className="p-3 border rounded-lg"
                />
                <input
                  type="number"
                  placeholder="Decimals (e.g., 18)"
                  value={newToken.decimals}
                  onChange={(e) => setNewToken({...newToken, decimals: e.target.value})}
                  className="p-3 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="CoinGecko ID (optional)"
                  value={newToken.coingecko_id}
                  onChange={(e) => setNewToken({...newToken, coingecko_id: e.target.value})}
                  className="p-3 border rounded-lg"
                />
              </div>
              <button
                onClick={addToken}
                className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
              >
                Add Token
              </button>
            </div>

            {/* Current Tokens */}
            {settings?.tokens && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Current Tokens</h3>
                <div className="space-y-2">
                  {settings.tokens.map((token) => (
                    <div key={token.token_id} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{token.symbol}</span> - {token.name}
                        <div className="text-sm text-gray-600">{token.chain} • {token.address}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        token.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {token.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chains Tab */}
        {activeTab === 'chains' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Current Chains</h3>
              {settings?.chains && (
                <div className="space-y-2">
                  {settings.chains.map((chain) => (
                    <div key={chain.chain_id} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{chain.name}</span> ({chain.chain_id})
                        <div className="text-sm text-gray-600">{chain.rpc_url}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        chain.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {chain.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default function App() {
  const [tokens, setTokens] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedToken, setSelectedToken] = useState('');
  const [cost, setCost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [paymentStep, setPaymentStep] = useState('ready'); // ready, paying, uploading
  const [paymentTxHash, setPaymentTxHash] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [switchingChain, setSwitchingChain] = useState(false);

  // Use wagmi hooks
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const getChainName = (chainId) => {
    switch (chainId) {
      case 1: return 'ethereum';
      case 137: return 'polygon';
      default: return 'ethereum';
    }
  };

  // Fetch user tokens with balances from ALL supported chains
  useEffect(() => {
    if (isConnected && address) {
      fetchUserTokensAllChains();
    } else {
      setTokens([]);
    }
  }, [isConnected, address]); // Remove chainId dependency - we query all chains

  const fetchUserTokensAllChains = async () => {
    try {
      setLoading(true);
      const allTokens = [];
      
      // Query balances from all supported chains
      for (const [chainName, chainConfig] of Object.entries(SUPPORTED_CHAINS)) {
        try {
          const response = await axios.get(`${API}/tokens/${address}?chain=${chainName}`);
          const chainTokens = response.data.tokens.map(token => ({
            ...token,
            chainId: chainConfig.id,
            chainName: chainConfig.name,
            displayName: `${token.symbol} (${chainConfig.name})`
          }));
          allTokens.push(...chainTokens);
        } catch (error) {
          console.error(`Failed to fetch tokens for ${chainName}:`, error);
        }
      }
      
      // Sort tokens by USD value (highest first)
      allTokens.sort((a, b) => b.balance_usd - a.balance_usd);
      setTokens(allTokens);
      
    } catch (error) {
      console.error('Failed to fetch user tokens from all chains:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate cost when file or token changes - with chain switching
  useEffect(() => {
    if (selectedFile && selectedToken && isConnected) {
      handleTokenSelection();
    }
  }, [selectedFile, selectedToken, address]);

  const handleTokenSelection = async () => {
    if (!selectedToken || !tokens.length) return;
    
    const selectedTokenData = tokens.find(t => t.id === selectedToken);
    if (!selectedTokenData) return;

    // Check if we need to switch chains
    if (selectedTokenData.chainId !== chainId) {
      try {
        setSwitchingChain(true);
        await switchChain({ chainId: selectedTokenData.chainId });
        // Wait a moment for chain switch to complete
        setTimeout(() => {
          setSwitchingChain(false);
          calculateCost();
        }, 1500);
      } catch (error) {
        console.error('Failed to switch chain:', error);
        setSwitchingChain(false);
        alert(`Please manually switch to ${selectedTokenData.chainName} to use this token`);
        return;
      }
    } else {
      calculateCost();
    }
  };

  const calculateCost = async () => {
    if (!selectedFile || !selectedToken || !address) return;

    setLoading(true);
    try {
      const token = tokens.find(t => t.id === selectedToken);
      const [chainId, tokenAddress] = selectedToken.split('-');

      const response = await axios.post(`${API}/calculate-cost`, {
        wallet_address: address,
        chain: chainId,
        token_address: tokenAddress,
        file_size: selectedFile.size,
        filename: selectedFile.name
      });

      setCost(response.data);
    } catch (error) {
      console.error('Failed to calculate cost:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedToken || !isConnected || !cost) return;

    // Payment is handled separately by PaymentProcessor
    // This function is called after payment is confirmed
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    setPaymentStep('uploading');

    try {
      // Step 1: Upload file (payment already confirmed)
      setUploadStatus('Uploading to IPFS...');
      setUploadProgress(50);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('wallet_address', address);
      formData.append('chain', getChainName(chainId));
      formData.append('token_address', selectedToken.split('-')[1]);
      formData.append('amount_paid', cost.token_amount);
      formData.append('transaction_hash', paymentTxHash || 'pending');

      setUploadStatus('Pinning to IPFS networks...');
      setUploadProgress(75);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus('Upload complete!');
      setUploadProgress(100);

      setTimeout(() => {
        setUploadResult(response.data);
        setUploading(false);
        setPaymentStep('ready');
        setPaymentCompleted(false);
        setPaymentTxHash(null);
      }, 1000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploading(false);
      setPaymentStep('ready');
      alert('Upload failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = (paymentData) => {
    setPaymentCompleted(true);
    setPaymentData(paymentData);
    setPaymentTxHash(paymentData.transferHash || paymentData.totalHash);
    
    // Automatically trigger upload after payment confirmation
    setTimeout(() => {
      handleUpload();
    }, 1000);
  };

  // Handle payment errors
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setPaymentStep('ready');
    alert('Payment failed: ' + (error.shortMessage || error.message));
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setSelectedToken('');
    setCost(null);
    setUploadResult(null);
    setUploadProgress(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Hero Image */}
          <div className="flex justify-center mb-6">
            <img 
              src="/android-chrome-512x512.png" 
              alt="Pingui Logo" 
              className="w-[200px] h-[200px] object-contain"
            />
          </div>
          
          <h1 className="text-lg text-gray-600">
            Upload files to IPFS with ease and pay with crypto
          </h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Wallet Connection */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Connect Your Wallet
            </h2>
            <WalletConnector />
          </div>

          {isConnected && !uploadResult && (
            <>
              {/* File Upload */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Select File
                </h2>
                <FileUploader 
                  onFileSelect={setSelectedFile} 
                  selectedFile={selectedFile} 
                />
              </div>

              {/* Token Selection */}
              {selectedFile && (
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Payment Method
                  </h2>
                  <TokenSelector
                    tokens={tokens}
                    selectedToken={selectedToken}
                    onSelect={setSelectedToken}
                    disabled={uploading}
                  />
                  {tokens.length === 0 && isConnected && (
                    <p className="text-sm text-gray-500 mt-2">
                      No tokens with balance found in your wallet for {getChainName(chainId)}
                    </p>
                  )}
                </div>
              )}

              {/* Cost Calculator */}
              {selectedFile && selectedToken && (
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <CostCalculator
                    file={selectedFile}
                    selectedToken={selectedToken}
                    tokens={tokens}
                    cost={cost}
                    loading={loading}
                  />
                </div>
              )}

              {/* Payment & Upload Section */}
              {selectedFile && selectedToken && cost && !uploading && !paymentCompleted && (
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <PaymentProcessor 
                    cost={cost}
                    selectedToken={selectedToken}
                    tokens={tokens}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                  />
                </div>
              )}

              {/* Upload Progress */}
              <UploadProgress
                uploading={uploading}
                progress={uploadProgress}
                status={uploadStatus}
              />
            </>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <UploadResult
                result={uploadResult}
                onNewUpload={resetUpload}
              />
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <FAQSection />

        {/* Footer */}
        <footer className="mt-16 text-center">
          <p className="text-sm text-gray-500">
            Made{' '}
            <button
              onClick={() => setShowAdmin(true)}
              className="text-gray-500 hover:text-gray-700 underline"
            >
              by
            </button>{' '}
            <a
              href="https://x.com/bitfalls"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              @bitfalls
            </a>
          </p>
        </footer>
      </div>

      {/* Admin Panel */}
      {showAdmin && (
        <AdminPanel onClose={() => setShowAdmin(false)} />
      )}
    </div>
  );
}
